import { useState } from 'react';
import { ApiError, api, useQuery } from '../api.js';
import { ErrorNote, Pill, Spinner, useToast } from '../ui.js';
import { CloseOut } from './CloseOut.js';
import { PlaneIcon } from '../icons.js';
import type { Finding, Reservation } from '../../../shared/types.js';
import { useLive } from '../live.js';

/** Wall-clock time, the way the rest of the ramp screen writes it. */
function clock(at: number): string {
  return new Date(at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Whether this account may check in yet, and why not. Decided by the server. */
interface CheckInWindow { open: boolean; opensAt: number; reason: string }

interface Flight extends Reservation {
  start: string; end: string; tiedown: string; findings: Finding[];
  checkIn: CheckInWindow;
}
interface Data {
  date: string; dayLabel: string; dateLabel: string; icao: string;
  role: 'student' | 'staff';
  flights: Flight[]; dualHours: number; groundHours: number;
}

/**
 * The ramp: today's flights, and the one action the person holding the phone
 * has on them.
 *
 * An instructor releases the aeroplane and closes the lesson out. A student
 * says they are here — which changes no status, because whether the flight goes
 * is still the instructor's call. The screen is the same either way, because
 * the two of them are standing next to each other looking at the same day.
 */
export function Mobile(): JSX.Element {
  const { data, loading, error, reload } = useQuery<Data>('/mobile/day');

  useLive('schedule', reload);
  const toast = useToast();
  const [busy, setBusy] = useState(0);
  const [closingId, setClosingId] = useState<number | null>(null);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const isStudent = data.role === 'student';

  const act = async (id: number, path: string, message: string, base = '/schedule/reservations') => {
    setBusy(id);
    try {
      await api.post(`${base}/${id}/${path}`);
      toast.ok(message);
      reload();
    } catch (err) {
      if (err instanceof ApiError && err.findings?.length) {
        toast.error('Blocked', err.findings.map((f) => f.title).join(' · '));
      } else {
        toast.error('That did not work', err instanceof Error ? err.message : undefined);
      }
    } finally {
      setBusy(0);
    }
  };

  return (
    <div className="ramp">
      <div className="ramp__device">
        {/* On a desk this is a drawing of a phone, so it wears a phone's status
            line. Held in the hand it is the phone, which has one already. */}
        <div className="ramp__status">
          <span style={{ fontWeight: 700 }}>
            {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
          </span>
          <span style={{ color: 'var(--header-muted)' }}>{data.icao}</span>
        </div>

        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted-2)' }}>{data.dayLabel}</div>
            <div style={{ fontSize: 21, fontWeight: 700, lineHeight: 1.15 }}>{data.dateLabel}</div>
          </div>
          <div className="small muted" style={{ textAlign: 'right' }}>
            {data.dualHours.toFixed(1)} flight<br />{data.groundHours.toFixed(1)} ground
          </div>
        </div>

        {data.flights.length === 0 && <div className="empty">Nothing on the schedule today.</div>}

        {data.flights.map((f) => {
          const worst = f.findings.find((x) => x.severity === 'conflict') ?? f.findings.find((x) => x.severity === 'review');
          return (
            <div key={f.id} style={{ padding: '11px 14px', borderBottom: '1px solid var(--border-3)' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{
                  width: 3, alignSelf: 'stretch', display: 'block', flex: 'none',
                  background: worst?.severity === 'conflict' ? 'var(--danger)'
                    : worst ? 'var(--pending-bd)' : 'var(--accent-bright)',
                }} />
                <div style={{ width: 54, flex: 'none' }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{f.start}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted-2)' }}>{f.end}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {isStudent
                      ? (f.lessonLabel || f.notes || f.kind)
                      : (f.studentName || f.notes || f.kind)}
                  </div>
                  <div className="small muted">
                    {/* An unassigned instructor is already a finding below;
                        saying it twice does not make it truer. */}
                    {isStudent ? f.instructorName : (f.lessonLabel || f.notes)}
                  </div>
                  <div style={{ fontSize: 11.5, marginTop: 2 }}>
                    {f.aircraftTail}{f.tiedown && ` · tie-down ${f.tiedown}`}
                  </div>
                  {worst && (
                    <div style={{ fontSize: 11.5, marginTop: 3, color: worst.severity === 'conflict' ? 'var(--conflict-c)' : 'var(--pending-c)' }}>
                      {worst.title}
                    </div>
                  )}
                </div>
                <div className="ramp__state">
                  {/* Authorized means the aeroplane is released and the lesson is
                      running. The mark breathes so a glance down the day finds
                      what is in the air without reading a word of it. */}
                  {f.status === 'authorized' && (
                    <PlaneIcon className="ramp__inflight" aria-label="Authorized — lesson under way" role="img" />
                  )}
                  <Pill>{f.status}</Pill>
                  {f.checkedInAt && (
                    <span className="ramp__here" title={`Checked in at ${clock(f.checkedInAt)}`}>
                      {/* The student already reads the full sentence under the
                          card; up here it only has to say which lesson. */}
                      {isStudent ? 'Checked in' : `Here · ${clock(f.checkedInAt)}`}
                    </span>
                  )}
                </div>
              </div>

              <div className="ramp__actions">
                {/*
                  * A student gets one button and it is never Authorize: whether
                  * the flight goes is the instructor's decision, and a screen
                  * that let a student release an aeroplane would be a lie about
                  * who is responsible for it. All they can say is that they are
                  * here, and only once the window is open.
                  */}
                {isStudent ? (
                  f.checkedInAt ? (
                    <span className="small muted" style={{ flex: 1, alignSelf: 'center' }}>
                      Checked in at {clock(f.checkedInAt)}. Your instructor has been told.
                    </span>
                  ) : f.checkIn.open ? (
                    <button
                      className="btn btn--primary ramp__btn"
                      disabled={busy === f.id}
                      onClick={() => act(f.id, 'check-in', 'Checked in · your instructor has been told', '/mobile/reservations')}
                    >
                      Check in
                    </button>
                  ) : (
                    <span className="small muted" style={{ flex: 1, alignSelf: 'center' }}>
                      {f.checkIn.reason}
                      {f.checkIn.opensAt > Date.now() && ` · from ${clock(f.checkIn.opensAt)}`}
                    </span>
                  )
                ) : (
                  <>
                    {(f.status === 'confirmed' || f.status === 'held') && (
                      <button
                        className="btn btn--primary ramp__btn"
                        disabled={busy === f.id}
                        onClick={() => act(f.id, 'authorize', `Authorized · ${f.studentName || f.aircraftTail}`)}
                      >
                        Authorize
                      </button>
                    )}
                    {f.status === 'authorized' && (
                      <button
                        className="btn btn--primary ramp__btn"
                        onClick={() => setClosingId(f.id)}
                      >
                        Close out the lesson
                      </button>
                    )}
                    {f.status === 'completed' && (
                      <span className="small muted" style={{ flex: 1, alignSelf: 'center' }}>
                        Closed out{f.hobbsEnd != null && f.hobbsStart != null && ` · ${(f.hobbsEnd - f.hobbsStart).toFixed(1)} hrs logged`}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        {loading && <div className="small muted" style={{ padding: 10 }}>Refreshing…</div>}
      </div>

      {closingId && (
        <CloseOut id={closingId} onClose={() => setClosingId(null)}
          onDone={() => { setClosingId(null); reload(); }} />
      )}

      {/* An explanation of the ramp screen, which is only worth reading from a
          desk. On the ramp you are looking at the thing itself. */}
      <div className="ramp__aside">
        <div style={{ fontSize: 17, fontWeight: 700 }}>
          {isStudent ? 'Your day at the field' : 'CFI day view'}
        </div>
        <p className="muted" style={{ fontSize: 12.5, textWrap: 'pretty' }}>
          {isStudent
            ? `Today's lessons, the aircraft and the tie-down to walk to. Check in when you arrive
               and your instructor knows you are here without being found and told.`
            : `The only screen a freelance CFI needs on the ramp: today's flights, the tail number and
               tie-down, and actions sized for a gloved thumb. Start Hobbs writes the block-out time,
               and blocking in prices the flight and writes the training record in one action.`}
        </p>
        <div style={{ height: 1, background: 'var(--border-2)', margin: '12px 0' }} />
        <p className="small muted" style={{ textWrap: 'pretty' }}>
          {isStudent
            ? `Check-in opens two hours before the lesson. It does not release the aeroplane —
               your instructor still authorizes the flight.`
            : `Findings ride along with each flight, so a grounded aircraft or a lapsed endorsement is
               visible at the aircraft, not back at the desk.`}
        </p>
      </div>
    </div>
  );
}
